import type { CSSProperties } from "react";
import { cn } from "../../lib/cn";

export type SkeletonVariant = "text" | "card" | "circle";

export interface SkeletonProps {
  variant?: SkeletonVariant;
  width?: number | string;
  height?: number | string;
  className?: string;
}

const variantClasses: Record<SkeletonVariant, string> = {
  text: "rounded-control h-4 w-full",
  card: "rounded-card w-full h-24",
  circle: "rounded-pill aspect-square",
};

/**
 * Always prefer this over a spinner — it reserves layout space, so nothing shifts once data
 * arrives.
 *
 * The shimmer is a gradient band swept across with `translateX`, not an animated
 * `background-position`: transforms are composited, background-position is not, and a table full
 * of skeleton cells repainting every frame is exactly where a loading state costs more than the
 * content it stands in for.
 *
 * Under prefers-reduced-motion the band is removed entirely (not merely slowed) and the block
 * stays a flat, still surface — a sweeping highlight is precisely the kind of repeating motion
 * that setting exists to suppress.
 */
export function Skeleton({ variant = "text", width, height, className }: SkeletonProps) {
  const style: CSSProperties = { width, height };
  return (
    <div
      role="status"
      aria-label="Loading"
      style={style}
      className={cn(
        "relative overflow-hidden rounded-control bg-surface-hover",
        variantClasses[variant],
        className,
      )}
    >
      <div
        aria-hidden
        className={cn(
          "absolute inset-0 -translate-x-full",
          "animate-shimmer motion-reduce:animate-none motion-reduce:hidden",
          // Theme-aware by construction: white at low alpha reads as a highlight on the dark
          // surface, and the same band reads as a soft sheen on the light one. A hardcoded
          // gradient colour would only work in one of the two.
          "bg-gradient-to-r from-transparent via-white/10 to-transparent",
        )}
      />
    </div>
  );
}
